import React, { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import {
    Badge,
    Button,
    RegionState,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';

import Card from '../../components/Card';
import { OrganizationKind } from '../../graphql/__generated__/schema';
import { AccountModulesDocument } from '../../graphql/__generated__/types';
import { refusalCode } from '../../graphql/refusalCode';
import { formatDate } from '../users/display';
import AccountModuleDialog from './AccountModuleDialog';
import { ModuleDirection } from './moduleRefusal';

interface Props {
    accountId: string;
    accountName: string;
    /** Freelance.com is `internal`; every client account is not. */
    kind: OrganizationKind | null | undefined;
}

/** The module a dialog is currently acting on, and which way the gesture goes. */
type Acting = { module: { code: string; label: string }; direction: ModuleDirection };

/**
 * The modules of **one account**: PCG-98.
 *
 * **Why it lives here, next to the portal access, on the account sheet.** A module
 * opened on an account is an object *of* that account: it is a row of
 * `organization_module`, keyed by `organization_id`, and its right is
 * `manage_accounts`, literally the one that opens the same account to the portal. So it
 * belongs where an account is the subject of the screen. Until PCG-141 that place was
 * the users list filtered on a single account, for want of an account sheet; it is the
 * sheet now, and the three panels read there as one sequence: may the account receive
 * portal users, what may it see, and what may it be shown inside the dashboards module.
 *
 * **Attention, the list is the whole referential, and never shortens.** Four of the six
 * modules have their tables and no reading path in the portal, so they cannot be
 * opened: they are shown all the same, disabled, with their reason. Two rows out of
 * six read as an incomplete referential, and an operator cannot ask about something
 * they have never seen. It is the same lesson as the non-grantable role of PCG-65 and
 * the empty catalogue of PCG-49, and the API is built for it: it returns every row
 * with `activatable` and `notActivatableReason` rather than filtering.
 *
 * **Attention, the two gestures are not disabled alike, and that is deliberate.** « Ouvrir »
 * is refused on a module with no screen: `enableAccountModule` refuses
 * `MODULE_NOT_DELIVERED`. « Fermer » never is: `disableAccountModule` deliberately
 * knows no such refusal, so that a row written by hand on such a module stays
 * erasable. A symmetric grey-out here would make it permanent.
 *
 * **Attention, nothing is hidden on a right the screen cannot check.** `viewer` exposes the
 * role but not the permissions behind it, so the front cannot know whether this
 * operator holds `manage_accounts`. The buttons therefore stay live and the refusal is
 * rendered *after* the click, naming the right that is missing: the one place the
 * « disabled with its reason » rule cannot be honoured.
 */
const AccountModulesPanel = ({ accountId, accountName, kind }: Props) => {
    const [acting, setActing] = useState<Acting | null>(null);

    /**
     * Skipped on Freelance.com: the internal organisation carries no portal module:
     * the back-office is a separate application and is gated by none of them: and
     * every gesture on it ends in `INTERNAL_ORGANIZATION_NOT_ALLOWED`.
     */
    const internal = kind === OrganizationKind.Internal;
    const { data, loading, error } = useQuery(AccountModulesDocument, {
        variables: { organizationId: accountId },
        skip: internal,
    });

    if (internal) {
        return null;
    }

    /**
     * Attention, `accountModules` is nullable, like almost everything in this schema. Null
     * means « the API did not say », which is not « the referential is empty »: and
     * an empty referential is impossible by construction, the rows being seeded by
     * migration. So the two are told apart below rather than both rendered as an
     * absence.
     */
    const modules = data?.accountModules ?? [];
    const openCount = modules.filter((module) => module.enabled).length;
    /**
     * Attention, the three states below key on **this**, and never on « did the field come
     * back non-null »: `Boolean([])` is true, so a referential answered as an empty
     * list would have been read as « an account with no module open »: a sentence
     * about an account, said about a query that returned nothing.
     */
    const total = modules.length;
    const someUndelivered = modules.some((module) => !module.activatable);

    const columns: TableHeaderCellProps[] = [
        { id: 'module', label: c('Account modules').t`Module`, minWidth: 240 },
        { id: 'state', label: c('Account modules').t`State on this account`, minWidth: 260 },
        { id: 'actions', label: '', minWidth: 150, textAlign: 'end' },
    ];

    /**
     * The refusal of the **read**. It is not one of the two mutations': this query
     * takes `manage_accounts` like them, but a screen that answers « vous ne pouvez
     * pas ouvrir » to a refused read tells the operator about a gesture they did not
     * make.
     */
    const readRefusal = () => {
        switch (refusalCode(error)) {
            case 'MISSING_PERMISSION':
                return c('Account modules')
                    .t`Your role does not let you manage accounts, and the modules of an account are read under that permission. They were refused, not answered: this account is not without modules, its modules were not read.`;
            case 'FORBIDDEN_OUT_OF_SCOPE':
                return c('Account modules')
                    .t`This account is outside your portfolio, so its modules were refused rather than answered.`;
            case 'INTERNAL_STAFF_ONLY':
                return c('Account modules')
                    .t`Reading what a client account contains belongs to the back-office, so a client-side role cannot carry it whatever its permissions.`;
            default:
                return c('Account modules')
                    .t`The modules of this account could not be read, so what it opens is unknown — which is not the same as opening nothing.`;
        }
    };

    return (
        <>
            <Card
                title={c('Account modules').t`Modules of this account`}
                hint={c('Account modules')
                    .t`A module is a section of the portal menu, opened account by account. Opening one grants no right on its own and does not apply to child accounts.`}
            >
                {/* THE RULE BEHIND A WHOLE FAMILY OF REFUSALS, SAID ONCE. Four of
                    the six modules refuse to open for the same reason, and repeating it
                    on four rows would turn a rule of the platform into four incidents.
                    The rows carry the short qualifier only. Conditional rather than
                    permanent: the day every module ships, this sentence stops being
                    true and stops being printed. */}
                {someUndelivered && (
                    <p className="mb-4 max-w-3xl text-xs text-text-600">
                        {c('Account modules')
                            .t`Some modules are in the menu but have no screen yet, so they cannot be opened. They stay listed rather than hidden.`}
                    </p>
                )}

                <Table>
                    <TableHeader resizable cells={columns} />
                    <TableBody loading={loading} loadingRows={3} loadingColumns={columns.length}>
                        {modules.map((module) => {
                            /**
                             * Attention, two states, and confusing them would make the screen say
                             * something false: `enabled` is about **this account**,
                             * `activatable` is about the **platform**: false everywhere at
                             * once, for a reason that has nothing to do with the account.
                             * Hence the two cells: the state column speaks of the account,
                             * the action column of what may be done and why not.
                             */
                            const undelivered = !module.activatable;
                            const reasonId = `fl-module-reason-${module.code}`;
                            // ttag refuses an expression inside a localized template, so
                            // the date is formatted before the string that holds it.
                            const grantedOn = formatDate(module.grantedAt);
                            return (
                                <TableRow key={module.code}>
                                    <TableCell>
                                        <span className="font-medium">{module.label}</span>
                                        {/* The technical code, which is what a screen, a log
                                            line and a support ticket all name it by. */}
                                        <span className="ml-2 font-mono text-xs text-text-500">{module.code}</span>
                                    </TableCell>
                                    <TableCell>
                                        {module.enabled ? (
                                            <span className="flex flex-col gap-1">
                                                <Badge category="success" size="small">
                                                    {c('Account modules').t`Open`}
                                                </Badge>
                                                {/* « Depuis quand et par qui », carried by the
                                                    row so that nobody has to open the audit
                                                    trail to answer it. Rendered only once the
                                                    date is there: « depuis, » would date the
                                                    opening of nothing. */}
                                                {module.grantedAt && (
                                                    <span className="text-xs text-text-600">
                                                        {c('Account modules').t`Since ${grantedOn}`}
                                                        {module.grantedByName ? ` · ${module.grantedByName}` : ''}
                                                    </span>
                                                )}
                                            </span>
                                        ) : (
                                            <Badge category="default" size="small">
                                                {c('Account modules').t`Closed`}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-end">
                                        {module.enabled ? (
                                            /* Never disabled on `activatable`. A row posted
                                               by hand on a module with no screen must stay
                                               erasable, and the API deliberately lets it be. */
                                            <Button
                                                category="weak"
                                                size="small"
                                                onClick={() =>
                                                    setActing({
                                                        module: { code: module.code, label: module.label },
                                                        direction: 'disable',
                                                    })
                                                }
                                            >
                                                {c('Account modules action').t`Close…`}
                                            </Button>
                                        ) : (
                                            <span className="inline-flex items-center justify-end gap-2">
                                                {/* The SHORT qualifier, beside the row it
                                                    disables: the rule itself is said once,
                                                    above. Same reading as the design system's
                                                    `SelectOption.disabledReason`: a control
                                                    that goes grey without a word reads as
                                                    broken rather than as refused. */}
                                                {undelivered && (
                                                    <span id={reasonId} className="text-xs text-text-500">
                                                        {c('Account modules').t`No screen yet`}
                                                    </span>
                                                )}
                                                <Button
                                                    category="weak"
                                                    size="small"
                                                    disabled={undelivered}
                                                    aria-describedby={undelivered ? reasonId : undefined}
                                                    onClick={() =>
                                                        setActing({
                                                            module: { code: module.code, label: module.label },
                                                            direction: 'enable',
                                                        })
                                                    }
                                                >
                                                    {c('Account modules action').t`Open…`}
                                                </Button>
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                {/* Three states, told apart. An error rendered as an absence would send
                    the operator away sure the account opens nothing. */}
                {error && (
                    <RegionState
                        tone="error"
                        title={c('Account modules').t`The modules of this account could not be read.`}
                        description={readRefusal()}
                    />
                )}
                {!loading && !error && total === 0 && (
                    <RegionState
                        tone="error"
                        title={c('Account modules').t`The referential of modules came back empty.`}
                        description={c('Account modules')
                            .t`It never is: its rows are seeded by migration and name the entries of the portal menu. So this is the sign of something wrong with the query, not of an account that has nothing — do not read it as « no module exists ».`}
                    />
                )}

                {/* NOT AN EMPTY LIST: the six rows are right there, all closed. It is
                    the ordinary state of almost every account in base, and left unsaid
                    the operator reads six greyed lines and cannot tell whether the
                    account is misconfigured or simply untouched. */}
                {!loading && !error && total > 0 && openCount === 0 && (
                    <p className="mt-4 max-w-3xl text-xs text-text-600" aria-live="polite">
                        {c('Account modules')
                            .t`No module is open on this account. That is normal for an account nobody has set up yet. Opening one makes it appear in its portal users' menu.`}
                    </p>
                )}
                {!loading && !error && total > 0 && openCount > 0 && (
                    <p className="mt-4 max-w-3xl text-xs text-text-600" aria-live="polite">
                        {/* The two numbers are data, never a hard-coded « sur 6 »: the
                            referential grows, and a screen stating its size from memory
                            is a wrong number written confidently. */}
                        {ngettext(
                            msgid`${openCount} module is open on this account, out of the ${total} the portal menu names.`,
                            `${openCount} modules are open on this account, out of the ${total} the portal menu names.`,
                            openCount
                        )}
                    </p>
                )}
            </Card>

            {acting && (
                <AccountModuleDialog
                    accountId={accountId}
                    accountName={accountName}
                    module={acting.module}
                    direction={acting.direction}
                    onClose={() => setActing(null)}
                />
            )}
        </>
    );
};

export default AccountModulesPanel;
